import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  Button,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TodayIcon from '@mui/icons-material/Today';
import SavingsIcon from '@mui/icons-material/Savings';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';
import MenuItem from '@mui/material/MenuItem';
import { clientService } from '../services/client.service';
import { salaryService } from '../services/salary.service';
import { marketService } from '../services/market.service';
import { useAuth } from '../contexts/AuthContext';
import { Client, Employee, PortfolioSummary, Trade, Payout, TradeType } from '../types';
import { formatCurrency, formatPercentage, formatDate, formatIndianNumber, toTitleCase, getTodayDate, tradeBucketIstDay } from '../utils/formatters';
import { dailyBrokerageContribution } from '../utils/brokerage';
// RabbitScoreCard replaced with compact inline strip
import PageHeader from '../components/common/PageHeader';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

interface EditClientForm {
  name: string;
  initialCapital: string;
  brokerClientCode: string;
  stockRate: string;
  optionRate: string;
  intraStockRate: string;
  futureRate: string;
  isActive: boolean;
  referredByEmployeeId: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box pt={3}>{children}</Box>}
    </div>
  );
}

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const { isAdmin, isSuperAdmin, isHR } = useAuth();
  const canEdit = isAdmin || isSuperAdmin || isHR;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [openPositions, setOpenPositions] = useState<Trade[]>([]);
  const [recentTrades, setRecentTrades] = useState<Trade[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [tabValue, setTabValue] = useState(0);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Add Funds dialog state
  const [addFundsOpen, setAddFundsOpen] = useState(false);
  const [addingFunds, setAddingFunds] = useState(false);
  const [addFundsAmount, setAddFundsAmount] = useState('');
  const [addFundsNotes, setAddFundsNotes] = useState('');
  const [addFundsDate, setAddFundsDate] = useState<string>('');

  const [editForm, setEditForm] = useState<EditClientForm>({
    name: '',
    initialCapital: '',
    brokerClientCode: '',
    stockRate: '',
    optionRate: '',
    intraStockRate: '',
    futureRate: '',
    isActive: true,
    referredByEmployeeId: '',
  });
  const [employees, setEmployees] = useState<Employee[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const [clientData, portfolioData] = await Promise.all([
          clientService.getClientById(id),
          clientService.getClientPortfolio(id),
        ]);
        setClient(clientData);
        setPortfolio(portfolioData.summary);
        setOpenPositions(portfolioData.openPositions);
        setRecentTrades(portfolioData.recentTrades);
        setPayouts(portfolioData.payouts);
      } catch (err: any) {
        setError(err.message || 'Failed to load client data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    salaryService.getEmployees(true).then((data: any) => setEmployees(data.employees || data || [])).catch(() => {});
  }, [id]);

  // Live price refresh for open positions (same pattern as TradingDashboard)
  const refreshPrices = useCallback(async () => {
    const scrips = [...new Set(openPositions.filter((t) => t.status === 'OPEN' && !t.isDeleted).map((t) => t.scrip).filter(Boolean))];
    if (scrips.length === 0) return;
    try {
      const { prices } = await marketService.getQuotes(scrips);
      setOpenPositions((prev) =>
        prev.map((t) => {
          if (t.status === 'OPEN' && prices[t.scrip]) {
            const livePrice = prices[t.scrip].price;
            if (!livePrice || livePrice <= 0) return t;
            const balanceLots = t.purchaseLots - (t.saleLots || 0);
            const investedAmount = balanceLots * t.lotSize * t.purchasePrice;
            const currentValue = livePrice * balanceLots * t.lotSize;
            const isSellFirst = t.direction === 'SELL';
            const unrealizedPnL = isSellFirst ? (investedAmount - currentValue) : (currentValue - investedAmount);
            return { ...t, currentPrice: livePrice, currentValue, unrealizedPnL, totalPnL: (t.realizedPnL || 0) + unrealizedPnL };
          }
          return t;
        })
      );
    } catch { /* non-fatal */ }
  }, [openPositions]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const istHour = (now.getUTCHours() + 5) % 24 + (now.getUTCMinutes() + 30 >= 60 ? 1 : 0);
      const istMin = (now.getUTCMinutes() + 30) % 60;
      const day = now.getUTCDay();
      if (day >= 1 && day <= 5 && (istHour > 9 || (istHour === 9 && istMin >= 0)) && (istHour < 15 || (istHour === 15 && istMin <= 35))) {
        refreshPrices();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [refreshPrices]);

  const handleOpenEditDialog = () => {
    if (client) {
      setEditForm({
        name: client.name,
        initialCapital: client.initialCapital.toString(),
        brokerClientCode: client.brokerClientCode || '',
        stockRate: ((client.brokerageRates?.stock || 0.003) * 100).toString(),
        intraStockRate: ((client.brokerageRates?.intraStock || 0.0003) * 100).toString(),
        futureRate: ((client.brokerageRates?.future || 0.0003) * 100).toString(),
        optionRate: (client.brokerageRates?.option || 100).toString(),
        isActive: client.isActive,
        referredByEmployeeId: client.referredByEmployeeId || '',
      });
      setEditDialogOpen(true);
    }
  };

  const handleUpdateClient = async () => {
    if (!id || !editForm.name || !editForm.initialCapital) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setUpdating(true);
      const selectedEmployee = employees.find(e => e.employeeId === editForm.referredByEmployeeId);
      const updatedClient = await clientService.updateClient(id, {
        name: toTitleCase(editForm.name),
        initialCapital: parseFloat(editForm.initialCapital),
        brokerClientCode: editForm.brokerClientCode.trim() || undefined,
        brokerageRates: {
          stock: parseFloat(editForm.stockRate) / 100 || 0.003,
          option: parseFloat(editForm.optionRate) || 100,
          intraStock: parseFloat(editForm.intraStockRate) / 100 || 0.0003,
          future: parseFloat(editForm.futureRate) / 100 || 0.0003,
        },
        referredByEmployeeId: editForm.referredByEmployeeId || undefined,
        referredByName: selectedEmployee?.name || undefined,
      });

      // If active status changed, toggle it
      if (client && client.isActive !== editForm.isActive) {
        await clientService.toggleClientActive(id);
        updatedClient.isActive = editForm.isActive;
      }

      setClient(updatedClient);
      setSuccess('Client updated successfully!');
      setEditDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update client');
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenAddFunds = () => {
    setAddFundsAmount('');
    setAddFundsNotes('');
    setAddFundsDate('');
    setAddFundsOpen(true);
  };

  const handleAddFunds = async () => {
    if (!id || !client) return;
    const amt = parseFloat(addFundsAmount);
    if (!isFinite(amt) || amt <= 0) {
      setError('Enter a positive amount.');
      return;
    }
    setAddingFunds(true);
    setError(null);
    try {
      const result = await clientService.addFunds(id, {
        amount: amt,
        date: addFundsDate || undefined,
        notes: addFundsNotes.trim() || undefined,
      });
      setClient(result.updatedClient);
      setSuccess(`Added ${new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt)} to ${client.name}'s capital.`);
      setAddFundsOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add funds');
    } finally {
      setAddingFunds(false);
    }
  };

  const getPnLColor = (value: number) => (value >= 0 ? '#059669' : '#DC2626');

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box mb={3} display="flex" alignItems="center" gap={1.5}>
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 1.5 }} />
          <Box flex={1}>
            <Skeleton variant="text" width={80} height={14} />
            <Skeleton variant="text" width={260} height={32} />
            <Skeleton variant="text" width={360} height={18} />
          </Box>
        </Box>
        <Grid container spacing={2.5} mb={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={6} sm={4} md={2} key={i}>
              <Skeleton variant="rounded" height={110} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={68} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!client || !portfolio) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <PageHeader eyebrow="Management" title="Client not found" fallbackPath="/clients" />
        <Alert severity="error">We couldn't find this client. It may have been removed.</Alert>
      </Box>
    );
  }

  // All realized P&L from all trades (deduplicated)
  const allRealizedPnL = [...openPositions, ...recentTrades]
    .filter((t, i, arr) => arr.findIndex(x => x.tradeId === t.tradeId) === i)
    .filter(t => !t.isDeleted)
    .reduce((sum, t) => sum + (t.realizedPnL || 0), 0);
  const allUnrealizedPnL = openPositions.reduce((sum, t) => sum + (t.unrealizedPnL || 0), 0);

  // Running Capital = Initial Capital ± 100% of ALL booked profit/loss (actual total cash earned/lost)
  const runningCapital = client.initialCapital + allRealizedPnL;

  // Total open invested (all types, for display)
  const totalOpenInvested = openPositions.reduce((sum, t) => sum + (t.investedAmount || 0), 0);

  // Available Cash = Running Capital - ALL Open Invested ± Futures/MTF unrealized
  // Realized P&L is already included via runningCapital — only add the
  // mark-to-market UNREALIZED leg here for futures/MTF (those are MTM-settled
  // daily so unrealized affects cash). Adding realized again would double-count.
  const futMtfMtm = openPositions
    .filter(t => t.tradeType === TradeType.FUTURE || t.isMTF)
    .reduce((sum, t) => sum + (t.unrealizedPnL || 0), 0);
  const availableCash = runningCapital - totalOpenInvested + futMtfMtm;

  // Current Value = Initial Capital + ALL P&L (realized + unrealized) = total portfolio worth
  const currentValue = client.initialCapital + allRealizedPnL + allUnrealizedPnL;

  const memberSinceDate = client.createdAt
    ? new Date(client.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'N/A';

  // -----------------------------------------------------------------
  // Brokerage — compute per-segment totals from this client's trades.
  // Uses the same logic pattern as the dashboard's period analytics.
  // -----------------------------------------------------------------
  const allClientTrades = [...openPositions, ...recentTrades]
    .filter((t, i, arr) => arr.findIndex((x) => x.tradeId === t.tradeId) === i)
    .filter((t) => !t.isDeleted);

  const rates = client.brokerageRates || { stock: 0.003, intraStock: 0.0003, future: 0.0003, option: 100 };
  const brk = { equity: 0, intra: 0, future: 0, option: 0, total: 0 };
  // Bajaj-style daily attribution: every leg ever opened or closed for this
  // client contributes its commission. Open buy-legs are now included
  // (delivery rate, since held past 3:30 PM IST). Same convention as the
  // Trading Dashboard Client Breakdown table.
  for (const t of allClientTrades) {
    const c = dailyBrokerageContribution(t, rates as any);
    const amount = c.buy + c.sell;
    if (amount === 0) continue;
    if (c.category === 'option') brk.option += amount;
    else if (c.category === 'future') brk.future += amount;
    else if (c.category === 'intra') brk.intra += amount;
    else brk.equity += amount;
  }
  brk.total = brk.equity + brk.intra + brk.future + brk.option;

  return (
    <Box sx={{ pb: 3 }}>
      {/* Error Alert */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Alert */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Premium Page Header */}
      <PageHeader
        eyebrow="Client"
        title={client.name}
        subtitle={`Initial Capital ${formatCurrency(client.initialCapital)}  ·  Member since ${memberSinceDate}${client.referredByName ? `  ·  Agent ${client.referredByName}` : ''}`}
        fallbackPath="/clients"
        actions={
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={client.isActive ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.04em',
                bgcolor: client.isActive ? '#D1FAE5' : '#F1F5F9',
                color: client.isActive ? '#047857' : '#475569',
              }}
            />
            {canEdit && (
              <>
                <Button
                  startIcon={<AccountBalanceWalletIcon sx={{ fontSize: 18 }} />}
                  onClick={handleOpenAddFunds}
                  variant="outlined"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    color: '#0A0A0A',
                    borderColor: '#E5E5E5',
                    bgcolor: '#FFFFFF',
                    '&:hover': { borderColor: '#A3A3A3', bgcolor: '#FAFAFA' },
                  }}
                >
                  Add Funds
                </Button>
                <Button
                  startIcon={<EditIcon />}
                  onClick={handleOpenEditDialog}
                  variant="contained"
                  size="small"
                  sx={{
                    bgcolor: '#0A0A0A',
                    color: '#FFFFFF',
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#262626', boxShadow: 'none' },
                  }}
                >
                  Edit Client
                </Button>
              </>
            )}
          </Box>
        }
      />

      {(() => {
        const metricCards: {
          label: string;
          value: string;
          icon: React.ReactNode;
          accent: string;
          accentBg: string;
          valueColor?: string;
        }[] = [
          {
            label: 'Initial Capital',
            value: formatIndianNumber(client.initialCapital),
            icon: <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />,
            accent: '#2563EB',
            accentBg: '#DBEAFE',
          },
          {
            label: 'Available Cash',
            value: formatIndianNumber(availableCash),
            icon: <SavingsIcon sx={{ fontSize: 20 }} />,
            accent: availableCash >= 0 ? '#059669' : '#DC2626',
            accentBg: availableCash >= 0 ? '#D1FAE5' : '#FEE2E2',
            valueColor: availableCash >= 0 ? '#059669' : '#DC2626',
          },
          {
            label: 'Invested (Open)',
            value: formatIndianNumber(totalOpenInvested),
            icon: <BarChartIcon sx={{ fontSize: 20 }} />,
            accent: '#4F46E5',
            accentBg: '#EEF2FF',
          },
          {
            label: 'Current Value',
            value: formatIndianNumber(currentValue),
            icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
            accent: currentValue >= client.initialCapital ? '#059669' : '#DC2626',
            accentBg: currentValue >= client.initialCapital ? '#D1FAE5' : '#FEE2E2',
          },
          {
            label: 'Realized P&L',
            value: formatIndianNumber(portfolio.totalRealizedPnL),
            icon: <ShowChartIcon sx={{ fontSize: 20 }} />,
            accent: getPnLColor(portfolio.totalRealizedPnL),
            accentBg: portfolio.totalRealizedPnL >= 0 ? '#D1FAE5' : '#FEE2E2',
            valueColor: getPnLColor(portfolio.totalRealizedPnL),
          },
          {
            label: 'Unrealized P&L',
            value: formatIndianNumber(portfolio.totalUnrealizedPnL),
            icon: <TimelineIcon sx={{ fontSize: 20 }} />,
            accent: getPnLColor(portfolio.totalUnrealizedPnL),
            accentBg: portfolio.totalUnrealizedPnL >= 0 ? '#D1FAE5' : '#FEE2E2',
            valueColor: getPnLColor(portfolio.totalUnrealizedPnL),
          },
        ];
        return (
          <Grid container spacing={2.5} mb={3}>
            {metricCards.map((card) => (
              <Grid item xs={6} sm={4} md={2} key={card.label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#CBD5E1',
                      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.25}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#64748B',
                        letterSpacing: '0.04em',
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      {card.label}
                    </Typography>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1.5,
                        bgcolor: card.accentBg,
                        color: card.accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {card.icon}
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '1.15rem',
                      fontWeight: 700,
                      color: card.valueColor || '#0F172A',
                      letterSpacing: '-0.01em',
                      fontFeatureSettings: '"tnum"',
                      lineHeight: 1.2,
                    }}
                  >
                    {card.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        );
      })()}

      {/* Brokerage breakdown strip + summary chips */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: '#FFFFFF',
          border: '1px solid #E2E8F0',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.25} mb={1.5}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.25,
              bgcolor: '#FEF3C7',
              color: '#D97706',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0F172A' }}>
            Brokerage Summary
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Total
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#D97706', fontFeatureSettings: '"tnum"' }}>
            {formatCurrency(brk.total)}
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          {[
            { label: 'Equity Delivery', val: brk.equity, rate: `${((rates.stock ?? 0.003) * 100).toFixed(2)}%` },
            { label: 'Intraday', val: brk.intra, rate: `${((rates.intraStock ?? 0.0003) * 100).toFixed(3)}%` },
            { label: 'Futures', val: brk.future, rate: `${((rates.future ?? 0.0003) * 100).toFixed(3)}%` },
            { label: 'Options', val: brk.option, rate: `₹${rates.option ?? 100}/lot` },
          ].map((b) => (
            <Grid item xs={6} md={3} key={b.label}>
              <Box
                sx={{
                  p: 1.25,
                  bgcolor: '#F8FAFC',
                  borderRadius: 1.5,
                  border: '1px solid #E2E8F0',
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.25}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {b.label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94A3B8' }}>
                    {b.rate}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#D97706', fontFeatureSettings: '"tnum"' }}>
                  {formatCurrency(b.val)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Quick stats strip */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 1.5,
          borderRadius: 2,
          bgcolor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {[
          { label: 'Running Capital', value: formatIndianNumber(runningCapital), color: '#4F46E5' },
          { label: 'Open Positions', value: portfolio.openPositionsCount.toString(), color: '#2563EB' },
          { label: 'Closed Trades', value: portfolio.closedTradesCount.toString(), color: '#64748B' },
          {
            label: 'Total P&L',
            value: `${formatIndianNumber(portfolio.totalPnL)} (${formatPercentage(portfolio.pnlPercentage)})`,
            color: getPnLColor(portfolio.totalPnL),
          },
        ].map((s, i) => (
          <React.Fragment key={s.label}>
            {i > 0 && <Box sx={{ width: '1px', height: 22, bgcolor: '#E2E8F0' }} />}
            <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 0.1 }}>
              <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: s.color, fontFeatureSettings: '"tnum"' }}>
                {s.value}
              </Typography>
            </Box>
          </React.Fragment>
        ))}
      </Paper>

      {/* Compact RABBIT SCORE strip */}
      {portfolio.rabbitScore && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 1.5,
            borderRadius: 2,
            bgcolor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                background:
                  portfolio.rabbitScore.colorCode === 'green' ? 'linear-gradient(135deg, #059669, #10B981)' :
                  portfolio.rabbitScore.colorCode === 'yellow' ? 'linear-gradient(135deg, #D97706, #F59E0B)' :
                  portfolio.rabbitScore.colorCode === 'red' ? 'linear-gradient(135deg, #DC2626, #EF4444)' :
                  'linear-gradient(135deg, #6B7280, #9CA3AF)',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: '0.88rem',
                fontFeatureSettings: '"tnum"',
              }}
            >
              {portfolio.rabbitScore.score.toFixed(1)}
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1 }}>
                Rabbit Score
              </Typography>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0F172A', lineHeight: 1.2 }}>
                {portfolio.rabbitScore.score >= 5.0 ? 'Excellent' :
                 portfolio.rabbitScore.score >= 2.0 ? 'Good' :
                 portfolio.rabbitScore.score >= 1.0 ? 'Average' :
                 portfolio.rabbitScore.score >= 0.5 ? 'Below Average' : 'Poor'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ width: '1px', height: 28, bgcolor: '#E2E8F0' }} />
          {[
            { label: 'Positive', val: portfolio.rabbitScore.deals.positive, color: '#059669' },
            { label: 'Negative', val: portfolio.rabbitScore.deals.negative, color: '#DC2626' },
            { label: 'Range', val: portfolio.rabbitScore.deals.range, color: '#D97706' },
            { label: 'Total', val: portfolio.rabbitScore.deals.total, color: '#0F172A' },
          ].map((d) => (
            <Box key={d.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.1, minWidth: 50 }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {d.label}
              </Typography>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: d.color, fontFeatureSettings: '"tnum"' }}>
                {d.val}
              </Typography>
            </Box>
          ))}
          <Box sx={{ flex: 1 }} />
          <Chip
            label={portfolio.rabbitScore.interpretation}
            size="small"
            sx={{
              fontSize: '0.68rem',
              fontWeight: 700,
              bgcolor:
                portfolio.rabbitScore.colorCode === 'green' ? '#D1FAE5' :
                portfolio.rabbitScore.colorCode === 'yellow' ? '#FEF3C7' : '#FEE2E2',
              color:
                portfolio.rabbitScore.colorCode === 'green' ? '#047857' :
                portfolio.rabbitScore.colorCode === 'yellow' ? '#B45309' : '#B91C1C',
            }}
          />
        </Paper>
      )}

      <Grid container spacing={3}>

        {/* Tabs */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 2, pt: 1 }}>
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                sx={{
                  minHeight: 40,
                  borderBottom: '1px solid #E2E8F0',
                  '& .MuiTab-root': {
                    minHeight: 40,
                    py: 1,
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: '#64748B',
                    textTransform: 'none',
                    letterSpacing: '-0.01em',
                    '&.Mui-selected': { color: '#0F172A' },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#4F46E5',
                    height: 2.5,
                    borderRadius: 1.25,
                  },
                }}
              >
                <Tab label={`Open Positions (${portfolio.openPositionsCount})`} />
                <Tab label="Recent Trades" />
                <Tab icon={<TodayIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Today's Trades" />
                <Tab label={`Payouts (${payouts.length})`} />
              </Tabs>
            </Box>
            <Box sx={{ p: 2 }}>

              {/* Open Positions Tab */}
              <TabPanel value={tabValue} index={0}>
                {openPositions.length === 0 ? (
                  <Typography color="text.secondary">No open positions</Typography>
                ) : (
                  <Box overflow="auto">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {['Scrip', 'Type', 'Lots', 'Avg Price', 'Invested', 'CMP', 'Current', 'P&L', 'P&L %'].map((h, i) => (
                            <th key={h} style={{ textAlign: i < 2 ? 'left' : 'right', padding: '10px 8px', borderBottom: '1px solid #E2E8F0', fontSize: '0.68rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {openPositions.map((trade) => {
                          const bal = (trade.balanceLots ?? trade.purchaseLots) || 0;
                          const ls = trade.lotSize || 1;
                          const invested = bal * ls * trade.purchasePrice;
                          const cmp = trade.currentPrice || trade.purchasePrice;
                          const current = cmp * bal * ls;
                          // Open rows display unrealized P&L only. Realized
                          // P&L from previously-sold lots belongs to the
                          // "Recent Trades" / portfolio totals, not an open row.
                          const isSellFirst = trade.direction === 'SELL';
                          const pnl = isSellFirst ? invested - current : current - invested;
                          const pnlPct = invested > 0 ? (pnl / invested) * 100 : 0;
                          return (
                            <tr key={trade.tradeId} style={{ borderBottom: '1px solid #F1F5F9' }}>
                              <td style={{ padding: '10px 8px', fontWeight: 600, fontSize: '0.85rem', color: '#0F172A' }}>{trade.scrip}</td>
                              <td style={{ padding: '10px 8px', fontSize: '0.82rem', color: '#64748B' }}>{trade.tradeType}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, fontSize: '0.85rem', fontFeatureSettings: '"tnum"' }}>{bal}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '0.85rem', color: '#475569', fontFeatureSettings: '"tnum"' }}>{formatCurrency(trade.purchasePrice)}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '0.85rem', fontFeatureSettings: '"tnum"' }}>{formatCurrency(invested)}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, fontSize: '0.85rem', color: '#2563EB', fontFeatureSettings: '"tnum"' }}>{formatCurrency(cmp)}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '0.85rem', fontFeatureSettings: '"tnum"' }}>{formatCurrency(current)}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, fontSize: '0.85rem', color: getPnLColor(pnl), fontFeatureSettings: '"tnum"' }}>
                                {formatCurrency(pnl)}
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, fontSize: '0.85rem', color: getPnLColor(pnlPct), fontFeatureSettings: '"tnum"' }}>
                                {formatPercentage(pnlPct)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                )}
              </TabPanel>

              {/* Recent Trades Tab */}
              <TabPanel value={tabValue} index={1}>
                {recentTrades.length === 0 ? (
                  <Typography color="text.secondary">No recent trades</Typography>
                ) : (
                  <Box overflow="auto">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {['Date', 'Scrip', 'Type', 'Lots', 'Buy Price', 'Sell Price', 'Status', 'P&L'].map((h, i) => (
                            <th key={h} style={{ textAlign: i < 3 || h === 'Status' ? 'left' : 'right', padding: '10px 8px', borderBottom: '1px solid #E2E8F0', fontSize: '0.68rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {recentTrades.map((trade) => {
                          const isClosed = trade.status === 'CLOSED';
                          const pnl = isClosed
                            ? (trade.realizedPnL || trade.totalPnL || 0)
                            : (trade.unrealizedPnL || trade.totalPnL || 0);
                          return (
                            <tr key={trade.tradeId} style={{ borderBottom: '1px solid #F1F5F9', opacity: isClosed ? 0.85 : 1 }}>
                              <td style={{ padding: '10px 8px', fontSize: '0.82rem', color: '#475569', fontFeatureSettings: '"tnum"' }}>{formatDate(trade.date)}</td>
                              <td style={{ padding: '10px 8px', fontWeight: 600, fontSize: '0.85rem', color: '#0F172A' }}>{trade.scrip}</td>
                              <td style={{ padding: '10px 8px', fontSize: '0.82rem', color: '#64748B' }}>{trade.tradeType}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, fontSize: '0.85rem', fontFeatureSettings: '"tnum"' }}>{trade.purchaseLots}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '0.85rem', color: '#475569', fontFeatureSettings: '"tnum"' }}>{formatCurrency(trade.purchasePrice)}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '0.85rem', color: isClosed ? '#475569' : '#94A3B8', fontFeatureSettings: '"tnum"' }}>{isClosed ? formatCurrency(trade.salePrice || 0) : '—'}</td>
                              <td style={{ padding: '10px 8px' }}>
                                <Chip
                                  label={trade.status || 'N/A'}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.68rem',
                                    fontWeight: 700,
                                    bgcolor: isClosed ? '#F1F5F9' : '#DBEAFE',
                                    color: isClosed ? '#64748B' : '#2563EB',
                                  }}
                                />
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, fontSize: '0.85rem', color: getPnLColor(pnl), fontFeatureSettings: '"tnum"' }}>
                                {formatCurrency(pnl)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                )}
              </TabPanel>

              {/* Today's Trades Tab */}
              <TabPanel value={tabValue} index={2}>
                {(() => {
                  const today = getTodayDate();
                  const todaysTrades = recentTrades.filter((trade) => tradeBucketIstDay(trade) === today);
                  if (todaysTrades.length === 0) {
                    return <Typography color="text.secondary">No trades today</Typography>;
                  }
                  return (
                    <Box overflow="auto">
                      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Showing {todaysTrades.length} trade{todaysTrades.length !== 1 ? 's' : ''} for {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>Scrip</th>
                            <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>Type</th>
                            <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>Status</th>
                            <th style={{ textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>Lots</th>
                            <th style={{ textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>Buy Price</th>
                            <th style={{ textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>Invested</th>
                            <th style={{ textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>P&L</th>
                          </tr>
                        </thead>
                        <tbody>
                          {todaysTrades.map((trade) => (
                            <tr key={trade.tradeId}>
                              <td style={{ padding: '12px 8px', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>{trade.scrip}</td>
                              <td style={{ padding: '12px 8px', borderBottom: '1px solid #f0f0f0' }}>{trade.tradeType}</td>
                              <td style={{ padding: '12px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Chip label={trade.status || 'N/A'} size="small" color={trade.status === 'OPEN' ? 'info' : 'default'} />
                              </td>
                              <td style={{ padding: '12px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>{trade.purchaseLots}</td>
                              <td style={{ padding: '12px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>{formatCurrency(trade.purchasePrice)}</td>
                              <td style={{ padding: '12px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>{formatCurrency(trade.investedAmount)}</td>
                              <td style={{ padding: '12px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', color: getPnLColor(trade.totalPnL), fontWeight: 600 }}>
                                {formatCurrency(trade.totalPnL)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  );
                })()}
              </TabPanel>

              {/* Payouts Tab */}
              <TabPanel value={tabValue} index={3}>
                {payouts.length === 0 ? (
                  <Typography color="text.secondary">No payouts recorded</Typography>
                ) : (
                  <Box overflow="auto">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>Date</th>
                          <th style={{ textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>Amount</th>
                          <th style={{ textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>Loss C/F</th>
                          <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payouts.map((payout) => (
                          <tr key={payout.payoutId}>
                            <td style={{ padding: '12px 8px', borderBottom: '1px solid #f0f0f0' }}>{formatDate(payout.date)}</td>
                            <td style={{ padding: '12px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', fontWeight: 600 }}>
                              {formatCurrency(payout.amount)}
                            </td>
                            <td style={{ padding: '12px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                              {formatCurrency(payout.lossCarryForward)}
                            </td>
                            <td style={{ padding: '12px 8px', borderBottom: '1px solid #f0f0f0' }}>{payout.notes || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                )}
              </TabPanel>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add Funds Dialog */}
      <Dialog
        open={addFundsOpen}
        onClose={() => !addingFunds && setAddFundsOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontSize: '1.15rem', fontWeight: 600, color: '#0A0A0A', letterSpacing: '-0.01em' }}>
                Add Funds
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#737373', mt: 0.25 }}>
                Capital top-up for {client?.name}
              </Typography>
            </Box>
            <IconButton onClick={() => setAddFundsOpen(false)} size="small" disabled={addingFunds}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#FAFAFA', border: '1px solid #E5E5E5', borderRadius: 1 }}>
            <Typography sx={{ fontSize: '0.65rem', color: '#A3A3A3', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>
              Current Capital
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#0A0A0A', mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
              {client ? formatCurrency(client.initialCapital) : '—'}
            </Typography>
          </Box>
          <Stack spacing={2}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Amount to add"
              type="number"
              value={addFundsAmount}
              onChange={(e) => setAddFundsAmount(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Effective date"
              type="date"
              value={addFundsDate}
              onChange={(e) => setAddFundsDate(e.target.value)}
              helperText="Optional — defaults to today"
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Notes"
              multiline
              minRows={2}
              value={addFundsNotes}
              onChange={(e) => setAddFundsNotes(e.target.value)}
              placeholder="e.g. Top-up via NEFT 22-May, reference INF/0123"
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
            />
            {addFundsAmount && parseFloat(addFundsAmount) > 0 && client && (
              <Box sx={{ p: 1.5, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 1 }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#15803D', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>
                  New Capital After
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: '#166534', mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                  {formatCurrency(client.initialCapital + parseFloat(addFundsAmount))}
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setAddFundsOpen(false)}
            disabled={addingFunds}
            sx={{ color: '#737373', textTransform: 'none', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddFunds}
            disabled={addingFunds || !addFundsAmount || parseFloat(addFundsAmount) <= 0}
            sx={{
              bgcolor: '#0A0A0A',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none',
              px: 2.5,
              '&:hover': { bgcolor: '#262626', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#E5E5E5', color: '#A3A3A3' },
            }}
          >
            {addingFunds ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Add Funds'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" fontWeight={700}>
              Edit Client
            </Typography>
            <IconButton onClick={() => setEditDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Client Name"
                fullWidth
                required
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Enter client name"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Initial Capital"
                fullWidth
                required
                type="number"
                value={editForm.initialCapital}
                onChange={(e) => setEditForm({ ...editForm, initialCapital: e.target.value })}
                placeholder="1000000"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Broker Client Code"
                fullWidth
                value={editForm.brokerClientCode}
                onChange={(e) => setEditForm({ ...editForm, brokerClientCode: e.target.value.toUpperCase().trim() })}
                placeholder="e.g. PERI5510"
                helperText="Optional — broker's client identifier. Used to auto-route trade-book imports (Bajaj .xls)."
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#5E6C84', fontWeight: 600 }}>
                Brokerage Rates
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Equity Delivery (%)"
                fullWidth
                size="small"
                type="number"
                value={editForm.stockRate}
                onChange={(e) => setEditForm({ ...editForm, stockRate: e.target.value })}
                inputProps={{ step: '0.01', min: 0 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Intraday (%)"
                fullWidth
                size="small"
                type="number"
                value={editForm.intraStockRate}
                onChange={(e) => setEditForm({ ...editForm, intraStockRate: e.target.value })}
                inputProps={{ step: '0.001', min: 0 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Futures (%)"
                fullWidth
                size="small"
                type="number"
                value={editForm.futureRate}
                onChange={(e) => setEditForm({ ...editForm, futureRate: e.target.value })}
                inputProps={{ step: '0.001', min: 0 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Options (&#8377;/lot)"
                fullWidth
                size="small"
                type="number"
                value={editForm.optionRate}
                onChange={(e) => setEditForm({ ...editForm, optionRate: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">&#8377;</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#22C55E',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#22C55E',
                      },
                    }}
                  />
                }
                label={editForm.isActive ? 'Active' : 'Inactive'}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#5E6C84', fontWeight: 600 }}>
                Agent Referral
              </Typography>
              <TextField
                select
                fullWidth
                label="Referred By (Agent)"
                value={editForm.referredByEmployeeId}
                onChange={(e) => setEditForm({ ...editForm, referredByEmployeeId: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              >
                <MenuItem value="">None</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.employeeId} value={emp.employeeId}>
                    {emp.name} - {emp.designation}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: '#6B778C' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateClient}
            disabled={updating}
            sx={{
              px: 3,
              textTransform: 'none',
              bgcolor: '#0A0A0A',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#262626', boxShadow: 'none' },
            }}
          >
            {updating ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Update Client'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ClientDetail;
